import { observable } from 'mobx';
import type { DepartmentArgs } from './department/department/department';
import Department from './department/department/department';
import type { ChargingDepartmentShareArgs } from './department/charging_department_share';
import type { ProjectArgs } from './project/project';
import type { ProjectShareArgs } from './project/project_share';
import ChargingDepartmentShare from './department/charging_department_share';
import ProjectShare from './project/project_share';
import Project from './project/project';
import { ExpensesAccountType } from './expenses/expenses_account_type';
import type { ExpensesAccountTypeJson } from './expenses/expenses_account_type';

export type OrderItemMappingResponseArgs = {
  id: number;
  traveler_information_id: number;
  charging_department_id?: number;
  charging_department?: DepartmentArgs;
  charging_department_share_id?: number;
  charging_department_share?: ChargingDepartmentShareArgs;
  display_charging_department?: string;
  display_project?: string;
  project_id?: number;
  project?: ProjectArgs;
  project_share_id?: number;
  project_share?: ProjectShareArgs;
  expenses_account_type_id?: number;
  expenses_account_type?: ExpensesAccountTypeJson;
};

export type OrderItemMappingArgs = {
  id: number;
  travelerInformationId: number;
  chargingDepartmentId?: number;
  chargingDepartment?: Department;
  chargingDepartmentShareId?: number;
  chargingDepartmentShare?: ChargingDepartmentShare;
  displayChargingDepartment?: string;
  displayProject?: string;
  projectId?: number;
  project?: Project;
  projectShareId?: number;
  projectShare?: ProjectShare;
  expensesAccountTypeId?: number;
  expensesAccountType?: ExpensesAccountType;
};

export const convertOrderItemMappingResponseToArgs = (response: OrderItemMappingResponseArgs) => {
  const args: OrderItemMappingArgs = {
    id: response.id,
    travelerInformationId: response.traveler_information_id,
    chargingDepartmentId: response.charging_department_id,
    chargingDepartment: response.charging_department ? new Department(response.charging_department) : undefined,
    chargingDepartmentShareId: response.charging_department_share_id,
    chargingDepartmentShare: response.charging_department_share
      ? new ChargingDepartmentShare(response.charging_department_share)
      : undefined,
    displayChargingDepartment: response.display_charging_department,
    displayProject: response.display_project,
    projectId: response.project_id,
    project: response.project ? new Project(response.project) : undefined,
    projectShareId: response.project_share_id,
    projectShare: response.project_share ? new ProjectShare(response.project_share) : undefined,
    expensesAccountTypeId: response.expenses_account_type_id,
    expensesAccountType: response.expenses_account_type
      ? new ExpensesAccountType(response.expenses_account_type)
      : undefined
  };
  return args;
};

export default class OrderItemMapping {
  @observable id: number;
  @observable travelerInformationId: number;
  @observable chargingDepartmentId?: number;
  @observable chargingDepartment?: Department;
  @observable chargingDepartmentShareId?: number;
  @observable chargingDepartmentShare?: ChargingDepartmentShare;
  @observable displayChargingDepartment?: string;
  @observable displayProject?: string;
  @observable projectId?: number;
  @observable project?: Project;
  @observable projectShareId?: number;
  @observable projectShare?: ProjectShare;
  @observable expensesAccountTypeId?: number;
  @observable expensesAccountType?: ExpensesAccountType;

  constructor(args: OrderItemMappingArgs) {
    this.id = args.id;
    this.travelerInformationId = args.travelerInformationId;
    this.chargingDepartmentId = args.chargingDepartmentId;
    this.chargingDepartment = args.chargingDepartment;
    this.chargingDepartmentShareId = args.chargingDepartmentShareId;
    this.chargingDepartmentShare = args.chargingDepartmentShare;
    this.displayChargingDepartment = args.displayChargingDepartment;
    this.displayProject = args.displayProject;
    this.projectId = args.projectId;
    this.project = args.project;
    this.projectShareId = args.projectShareId;
    this.projectShare = args.projectShare;
    this.expensesAccountTypeId = args.expensesAccountTypeId;
    this.expensesAccountType = args.expensesAccountType;
  }

  get isChargingDepartmentExist() {
    return !!this.chargingDepartmentId;
  }

  get isProjectExist() {
    return !!this.projectId || !!this.projectShareId;
  }

  get isExpensesAccountTypeExist() {
    return !!this.expensesAccountTypeId;
  }
}
